"use client";

import React from "react";
import Image from "next/image";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import Box from "@sendsprint/ui-react/components/box";
import Typography from "@sendsprint/ui-react/components/typography";
import Container from "@sendsprint/ui-react/components/container";
import { transformFullBlog } from "@/utils/blog";
import useMedia from "@sendsprint/ui-react/hooks/useMedia";
import BlogCard from "../blogCard";
import { formatDate } from "@/utils/formatDate";
import BlogCategory from "../blogCategory";
import { resolveBlogDetails } from "../../utils/resolveBlogDetails";

interface Props {
  blogsData: any;
  categoryName: string;
}

export const EDITORS_CATEGORY = "Editor's Pick";

const EditorsPick = ({ blogsData, categoryName }: Props) => {
  const searchParams = useSearchParams();
  const searchValue = searchParams.get("search");

  const allBlogs = transformFullBlog(blogsData);
  const filteredBlogs = allBlogs.filter((item) =>
    item.category.includes(EDITORS_CATEGORY),
  );

  const firstBlog = filteredBlogs[0];
  const otherBlogs = filteredBlogs.slice(1);

  const { isMobile } = useMedia();
  const blogDate = formatDate(firstBlog?.date);

  const resolvedBlogDetails = resolveBlogDetails(firstBlog, {
    titleLength: 60,
  });

  if (!filteredBlogs.length || categoryName || searchValue) return null;

  return (
    <Box as="section">
      <Container className="ss:pb-20">
        <Typography as="h2" className="ss:mb-12 ss:font-semibold" variant="h4">
          Editor&apos;s Pick
        </Typography>
        {firstBlog && !isMobile && (
          <Link href={firstBlog?.url}>
            <Box className="ss:flex ss:hover:bg-primary1-500 ss:hover:text-neutral-50 ss:duration-200 ss:items-center ss:p-10 ss:gap-5 ss:rounded-xl ss:bg-neutral-100">
              <Box className="ss:flex-1">
                <Box className="ss:flex ss:w-max ss:items-center ss:gap-3 ss:flex-wrap">
                  {firstBlog?.category.map((item, index) => (
                    <BlogCategory category={item} key={index} />
                  ))}
                </Box>
                <Typography
                  as="h3"
                  variant="h5"
                  className="ss:mb-6 ss:font-semibold"
                >
                  {resolvedBlogDetails.title}
                </Typography>
                <Box className="ss:flex ss:items-center ss:gap-4">
                  <Image
                    alt={`${resolvedBlogDetails.authorText} picture`}
                    src={resolvedBlogDetails.authorUrl}
                    className="ss:h-10 ss:w-10 ss:rounded-full"
                    width={40}
                    height={40}
                    priority
                  />
                  <Box>
                    <Typography as="h6" className="ss:font-bold">
                      {resolvedBlogDetails.authorText}
                    </Typography>
                    <Typography as="p" className="" variant="paragraphSmall">
                      {blogDate}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box className="ss:flex-1">
                <Image
                  className="ss:rounded-lg ss:object-cover ss:overflow-hidden ss:w-full ss:h-100"
                  alt={firstBlog?.title}
                  src={firstBlog?.imageUrl}
                  width={400}
                  height={400}
                />
              </Box>
            </Box>
          </Link>
        )}
        {firstBlog && isMobile && <BlogCard {...firstBlog} />}
        <Box className="ss:grid ss:grid-cols-1 ss:md:grid-cols-2 ss:lg:grid-cols-3 ss:mt-8 ss:gap-8">
          {otherBlogs &&
            otherBlogs?.map((item) => <BlogCard key={item.id} {...item} />)}
        </Box>
      </Container>
    </Box>
  );
};

export default EditorsPick;
